import './commitment-details-sheet.scss';

import React, { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import {
  ActionCard,
  ActionCardBody,
  ActionCardSection,
} from '@sola/ark-core/molecules/action-card/action-card.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@sola/ark-core/molecules/sheet/sheet.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import {
  GraphQLCommitment,
  GraphQLCommitmentType,
  GraphQLPledge,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { getThStNdRd } from '@sola/shared-utils/misc.js';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.js';
import { paymentMethodsQuery } from '@sola/supporter-sdk/queries.js';
import {
  getCommitmentLabel,
  getCommitmentTitle,
  getFrequencyAbbr,
} from '@sola/supporter-sdk/utils/commitments.js';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar';
import {
  PaymentMethodIcon,
  getLabel,
} from '../../atoms/paymentmethod-icon/paymentmethod-icon';
import { SectionContent } from '../../molecules/section-content/section-content';
import { formatPledgeName } from '../../utils/pledges';

export interface CommitmentDetailsSheetProps {
  /**
   * This should only take in elements that resolve to html buttons.
   */
  isOpen?: boolean;
  onOpenChange?: (open: boolean, commitmentId: string) => void;
  openTrigger: ReactElement;
  commitment?: GraphQLCommitment | null;
  pledge?: GraphQLPledge | null;
}

export interface CommitmentDetailsProps {
  commitment: GraphQLCommitment;
}

export interface PledgeDetailsProps {
  pledge: GraphQLPledge;
}

interface CommitmentDetailsListItemProps {
  title: string;
  children: React.ReactNode;
}

export interface CommitmentDetailsSectionProps {
  title?: string;
  children: React.ReactNode;
}

interface CommitmentDetailsBodyProps {
  title: string;
  children: React.ReactNode;
}

const CommitmentDetailsListItem: React.FC<CommitmentDetailsListItemProps> = ({
  title,
  children,
}) => {
  return (
    <li className="label-md label-moderate label-neutral-mild">
      {title && (
        <span className="label-potent">
          {title}
          {': '}
        </span>
      )}
      <span>{children}</span>
    </li>
  );
};

export const CommitmentDetailsSection: React.FC<
  CommitmentDetailsSectionProps
> = ({ title, children }) => {
  return (
    <div className="CommitmentDetails__section py-comfy-xl">
      <SectionContent heading={title || ''}>
        <div className="flex flex-col gap-comfy-sm">{children}</div>
      </SectionContent>
    </div>
  );
};

export const CommitmentDetailsActionCard: React.FC = () => {
  const { t } = useTranslation(['my']);

  // TODO: Should this whole card be a link?
  return (
    <div className="pt-comfy-xl">
      <ActionCard>
        <ActionCardSection>
          <ActionCardBody>
            <div className="flex items-center gap-comfy-xs ">
              <div className="CommitmentDetails__icon shrink-0 rounded-md flex items-center justify-center">
                <ArkIcon iconName="message-question-square" size="18" />
              </div>

              <div>
                <h3 className="label-potent label-lg mb-snug-md">
                  {t('commitments.details.needHelp.title')}
                </h3>
                <p className="label-moderate label-neutral-mild">
                  {t('commitments.details.needHelp.description')}
                </p>
              </div>
            </div>

            <Link
              target="_blank"
              href={t('ctas.contactSupport.url')}
              className="CommitmentDetails__support flex items-center self-end"
            >
              <ArkIcon
                iconName="chevron-right"
                fontSize={24}
                className="label-foreground-moderate"
              />
            </Link>
          </ActionCardBody>
        </ActionCardSection>
      </ActionCard>
    </div>
  );
};

const CommitmentDetailsBody: React.FC<
  React.PropsWithChildren<CommitmentDetailsBodyProps>
> = ({ title, children }) => {
  return (
    <div className="CommitmentDetails flex flex-col">
      <h3 className="CommitmentDetails__title body-sm body-neutral-mild pb-comfy-xl">
        {title}
      </h3>
      {children}
    </div>
  );
};

export const PledgeDetails: React.FC<PledgeDetailsProps> = ({
  pledge,
}: PledgeDetailsProps) => {
  const { t } = useTranslation('my');

  return (
    <>
      <CommitmentDetailsSection
        title={t('commitments.category.financialPledges')}
      >
        {/* TODO: We need the photo URL for the Beyond Measure campaign logo - We'll want to hide the avatar if the pledgeName isn't beyond measure */}
        {/* <CommitmentAvatar size="lg" /> */}
        {pledge?.pledgeName && (
          <span className="label-lg label-potent">
            {formatPledgeName(pledge.pledgeName, t)}
          </span>
        )}

        <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">
          <CommitmentDetailsListItem
            title={t('commitments.pledges.details.totalAmount')}
          >
            {t('commitments.pledges.details.values.amount', {
              amount: pledge.totalPledgeAmount,
            })}
          </CommitmentDetailsListItem>
          <CommitmentDetailsListItem
            title={t('commitments.pledges.details.totalGiven')}
          >
            {t('commitments.pledges.details.values.amount', {
              amount: pledge.totalGivenToDate,
            })}
          </CommitmentDetailsListItem>
          <CommitmentDetailsListItem
            title={t('commitments.pledges.details.amountOutstanding')}
          >
            {t('commitments.pledges.details.values.amount', {
              amount: pledge.outstandingAmount,
            })}
          </CommitmentDetailsListItem>
        </ul>
      </CommitmentDetailsSection>

      {pledge?.lastInstallmentDate && pledge?.lastInstallmentAmount && (
        <CommitmentDetailsSection
          title={t('commitments.pledges.details.lastInstallment')}
        >
          <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">
            <CommitmentDetailsListItem
              title={t('commitments.pledges.details.installmentDate')}
            >
              {t('commitments.pledges.details.values.installmentDate', {
                date: pledge?.lastInstallmentDate,
              })}
            </CommitmentDetailsListItem>
            <CommitmentDetailsListItem
              title={t('commitments.pledges.details.amount')}
            >
              {t('commitments.pledges.details.values.amount', {
                amount: pledge?.lastInstallmentAmount,
              })}
            </CommitmentDetailsListItem>
          </ul>
        </CommitmentDetailsSection>
      )}

      {pledge?.nextInstallmentDate && pledge?.nextInstallmentAmount && (
        <CommitmentDetailsSection
          title={t('commitments.pledges.details.nextInstallment')}
        >
          <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">
            <CommitmentDetailsListItem
              title={t('commitments.pledges.details.installmentDate')}
            >
              {t('commitments.pledges.details.values.installmentDate', {
                date: pledge?.nextInstallmentDate,
              })}
            </CommitmentDetailsListItem>
            <CommitmentDetailsListItem
              title={t('commitments.pledges.details.amount')}
            >
              {t('commitments.pledges.details.values.amount', {
                amount: pledge?.nextInstallmentAmount,
              })}
            </CommitmentDetailsListItem>
          </ul>
        </CommitmentDetailsSection>
      )}
    </>
  );
};

export const CommitmentDetails: React.FC<CommitmentDetailsProps> = ({
  commitment,
}: CommitmentDetailsProps) => {
  const { t } = useTranslation(['my', 'common']);
  const { data, isFetching, isLoading, isPending, isError } =
    useSupporterQuery(paymentMethodsQuery);
  const paymentMethodForCommitment = useMemo(() => {
    if (!Array.isArray(data)) {
      return undefined;
    }

    // Once again, this shouldn't be needed once we tighten our supporter query types.
    return (data as GraphQLSupporterPaymentMethod[]).find(
      (pm) =>
        (pm as GraphQLSupporterPaymentMethod)?.id ===
        commitment.paymentMethodId,
    );
  }, [data, commitment.paymentMethodId]);

  const isShowingSkeletons = (isLoading || isFetching || isPending) && !isError;

  return (
    <>
      <CommitmentDetailsSection title={getCommitmentLabel(commitment, t)}>
        <CommitmentAvatar size="lg" commitment={commitment} />
        <span className="label-lg label-potent">
          {getCommitmentTitle(commitment, t)}
        </span>

        <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">
          {commitment.commitmentType ===
            GraphQLCommitmentType.ChildSponsorship && (
            <CommitmentDetailsListItem
              title={t('commitments.details.overview.list.id')}
            >
              {commitment.participant?.localId}
            </CommitmentDetailsListItem>
          )}
          <CommitmentDetailsListItem
            title={t('commitments.details.overview.list.amount')}
          >
            {t('common:numbers.currency', {
              amount: commitment.rate,
            })}
            <span>
              {getFrequencyAbbr(commitment.paymentFrequency || '', t)}
            </span>
          </CommitmentDetailsListItem>
          {!!commitment.isRecurring && (
            <CommitmentDetailsListItem
              title={t('commitments.details.overview.list.nextPayment')}
            >
              {t('commitments.details.overview.values.nextPayment', {
                date: commitment.paymentDateNext,
              })}
            </CommitmentDetailsListItem>
          )}
        </ul>
      </CommitmentDetailsSection>

      <CommitmentDetailsSection
        title={t('commitments.details.paymentMethod.title')}
      >
        {isShowingSkeletons && !data && <Skeleton height={100} width={250} />}
        {!isShowingSkeletons && isError && (
          <Alert
            variant="negative"
            title={t('commitments.details.paymentMethod.queryError.title')}
            message={t('commitments.details.paymentMethod.queryError.message')}
            action={
              <a href={t('ctas.contactSupport.url')}>
                {t('ctas.contactSupport.text')}
              </a>
            }
          />
        )}
        {!isShowingSkeletons && !isError && paymentMethodForCommitment && (
          <>
            <div className="flex gap-comfy-md items-center">
              <PaymentMethodIcon
                paymentMethod={
                  paymentMethodForCommitment as GraphQLSupporterPaymentMethod
                }
              />
              <span className="label-moderate label-lg">
                {getLabel(paymentMethodForCommitment, t)}
              </span>
            </div>
            <LinkStyle noUnderline inline>
              <Link href={t('commitments.details.paymentMethod.changeLink')}>
                {t('commitments.details.paymentMethod.changeLabel')}
              </Link>
            </LinkStyle>
          </>
        )}
      </CommitmentDetailsSection>

      {commitment.paymentPullDay && (
        <CommitmentDetailsSection
          title={t('commitments.details.paymentDay.title')}
        >
          <span className="label-moderate label-md label-neutral-mild">
            {t('commitments.details.paymentDay.paymentPullDayLabel', {
              day: getThStNdRd(+commitment.paymentPullDay),
            })}
          </span>
        </CommitmentDetailsSection>
      )}

      <CommitmentDetailsActionCard />
    </>
  );
};

export const CommitmentDetailsSheet: React.FC<
  React.PropsWithChildren<CommitmentDetailsSheetProps>
> = ({ isOpen = false, openTrigger, commitment, pledge, onOpenChange }) => {
  const { t } = useTranslation('my');
  const title = t('commitments.detailsSheet.heading');

  if (!commitment && !pledge) {
    return null;
  }

  return (
    <Sheet
      {...(isOpen !== undefined && { open: isOpen })}
      {...(onOpenChange &&
        commitment && {
          onOpenChange: (open) => {
            onOpenChange(open, commitment.id);
          },
        })}
      {...(onOpenChange &&
        pledge?.pledgeId && {
          onOpenChange: (open) => {
            onOpenChange(open, pledge.pledgeId!);
          },
        })}
    >
      <SheetTrigger>{openTrigger}</SheetTrigger>
      <SheetContent title={title}>
        <SheetHeader>{title}</SheetHeader>
        <SheetBody>
          <CommitmentDetailsBody
            title={
              commitment
                ? t('commitments.details.subTitle')
                : t('commitments.pledges.details.subTitle', {
                    name: pledge?.pledgeName,
                  })
            }
          >
            {commitment && <CommitmentDetails commitment={commitment} />}
            {pledge && <PledgeDetails pledge={pledge} />}
          </CommitmentDetailsBody>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
};
